import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { IRRIGATION_ACTIVITY_FLOW_METER_VALUE_TYPE_PREFIX } from '@semios/app-platform-values-helpers-and-types'
import type { TGroupedTableSection } from 'App/dashboards/_components/DashboardGroupedTable'
import { getAverageFlowRateGpm } from 'App/dashboards/_utils/getAverageFlowRateGpm'
import type moment from 'moment-timezone'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { apiFetch } from 'utils/apiFetch'
import { getIsoWithoutTheZ } from 'utils/getIsoWithoutTheZ'
import type { TPointExternalVendorNames } from 'utils/getPointExternalVendorNames'
import { getPointExternalVendorNames } from 'utils/getPointExternalVendorNames'

// One flow-meter device = one row. Cumulative flow (gallons) + on-duration (ms) come from the
// irrigationFlowMeter response metadata for the requested window.
export type TFlowMeterRow = {
  lngLat: string
  deviceName: string
  externalVendorNames: TPointExternalVendorNames | undefined
  cumulativeGallons: number | null
  onDurationMs: number | null
  averageFlowRateGpm: number | null
}

type TPropertyFlowMeters = {
  propertyId: number
  propertyName: string
  // Flow-meter device points (advertise an irrigationFlow* value type), sorted by name.
  devices: { lngLat: string; name: string; externalVendorNames: TPointExternalVendorNames | undefined }[]
}

const getPropertyFlowMeters = (
  properties: NonNullable<ReturnType<typeof fieldAssetStore.getState>['properties']>,
  propertyIds: number[],
): TPropertyFlowMeters[] =>
  propertyIds
    .map((propertyId) => {
      const property = properties[propertyId]

      if (!property) return null

      const devices = Object.values(property.points ?? {})
        .filter((p) =>
          (p.valuesTimeseries ?? []).some((vt) =>
            vt.includes(IRRIGATION_ACTIVITY_FLOW_METER_VALUE_TYPE_PREFIX),
          ),
        )
        .map((p) => ({
          lngLat: String(p.lngLat),
          name: p.name,
          externalVendorNames: getPointExternalVendorNames({ point: p, deviceKind: 'flowMeter' }),
        }))
        .sort((a, b) => a.name.localeCompare(b.name))

      // Every selected property gets a section even with no flow-meter devices (empty rows → a
      // per-property "no data" message), so the property isn't silently dropped from the widget.
      return { propertyId, propertyName: property.propertyName, devices }
    })
    .filter((p) => p !== null)

export type TFlowMeterRateResult = {
  sections: TGroupedTableSection<TFlowMeterRow>[]
  loading: boolean
  error: Error | null
  lastFetchedAt: Date | null
  refresh: () => void
}

// Fetches irrigationFlowMeter (points scope, DAILY) for every selected property's flow-meter devices
// in one /values call, then reads the window's cumulative flow + on-duration from each device's
// metadata. Mirrors apiArgsForIrrigationFlowMeter + the Hub flow-meter summary's metadata read.
export const useFlowMeterRate = ({
  propertyIds,
  dateFrom,
  dateTo,
  refreshToken = 0,
}: {
  propertyIds: number[]
  dateFrom: moment.Moment
  dateTo: moment.Moment
  refreshToken?: number
}): TFlowMeterRateResult => {
  const properties = fieldAssetStore.useSelector((s) => s.properties)
  const [data, setData] = useState<routes.Values.Response | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null)
  const [refreshNonce, setRefreshNonce] = useState(0)
  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), [])
  const dateFromMs = +dateFrom
  const dateToMs = +dateTo
  // Content key so a fresh propertyIds array each render doesn't churn the memo (and the fetch).
  const propertyIdsKey = propertyIds.join('|')

  const propertyFlowMeters = useMemo(
    () => (properties ? getPropertyFlowMeters(properties, propertyIds) : []),
    [properties, propertyIdsKey],
  )

  useEffect(() => {
    const lngLats = [...new Set(propertyFlowMeters.flatMap((p) => p.devices.map((d) => d.lngLat)))]

    if (lngLats.length === 0) {
      setData(null)

      setLoading(false)

      setError(null)

      return
    }

    let cancelled = false

    setLoading(true)

    setError(null)

    const body = {
      dateFrom: getIsoWithoutTheZ(dateFrom),
      dateTo: getIsoWithoutTheZ(dateTo),
      // Tag the call's source (as pest, Data Export and Property Playback do) so dashboard-driven
      // /values load is attributable in request logging. A declared optional enum on the Values
      // request schema (value-type-definitions/valuesTimeseriesTypes) that the API validates but
      // never consumes — so an invalid value 422s rather than being silently ignored.
      featureReferer: 'DASHBOARD',
      points: {
        lngLats,
        valuesRequested: { irrigationFlowMeter: { preferredAggregationInterval: 'DAILY' } },
      },
    } as routes.Values.Request

    apiFetch({ url: '/values', body })
      .then((res) => {
        if (cancelled) return

        setData(res)

        setLastFetchedAt(new Date())
      })
      .catch((err) => {
        if (cancelled) return

        setError(err instanceof Error ? err : new Error(String(err)))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [propertyFlowMeters, dateFromMs, dateToMs, refreshNonce, refreshToken])

  const sections = useMemo<TGroupedTableSection<TFlowMeterRow>[]>(() => {
    return propertyFlowMeters.map((p) => ({
      propertyId: p.propertyId,
      propertyName: p.propertyName,
      rows: p.devices.map((device) => {
        const metadata = data?.points?.[device.lngLat]?.values?.irrigationFlowMeter?.[0]?.metadata
        // The window's cumulative flow (distinct from the fixed 7-day/season/YTD metadata fields).
        const cumulativeGallons = metadata?.cumulativeFlowGallons ?? null
        const onDurationMs = metadata?.onDurationMillisecond ?? null

        return {
          lngLat: device.lngLat,
          deviceName: device.name,
          externalVendorNames: device.externalVendorNames,
          cumulativeGallons,
          onDurationMs,
          averageFlowRateGpm: getAverageFlowRateGpm(cumulativeGallons, onDurationMs),
        }
      }),
    }))
  }, [propertyFlowMeters, data])

  return useMemo(
    () => ({ sections, loading, error, lastFetchedAt, refresh }),
    [sections, loading, error, lastFetchedAt, refresh],
  )
}
